import { API } from "@swivl-mono/api-helper";
import { Button, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { loadGoogleSheetNames } from "./Onboarding.GoogleSheetImport";
import { normalizeValue } from "./Onboarding.OrgVariables";
import { styles } from "./shared/onboarding.styles";
import { extractSheetId, getColumnLetter } from "./shared/onboarding.utils";
import { ValidationError, validateRecord } from "./shared/validation";

export interface GoogleSheetData {
  headers: string[];
  records: Record<string, string>[];
}

interface TestModeResult {
  orgLevelVars: Array<{ key: string; value: string; columnName: string }>;
  locationLevelVars: Array<{
    locationId: string;
    locationName: string;
    variables: Array<{ key: string; value: string; columnName: string }>;
  }>;
  summary: {
    totalOrgVars: number;
    totalLocationVars: number;
    totalLocations: number;
    skippedColumns: string[];
  };
}

interface ImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}

// Column mapping from Google Sheet headers to variable keys
export const COLUMN_MAPPING: Record<string, string> = {
  "Location Name": "Name",
  Address: "Address",
  City: "City",
  State: "State",
  "Postal Code": "PostalCode",
  Country: "CountryCode",
  "Location Email": "Email",
  "Location Phone #": "Phone",
  "Access Hours": "AccessHours",
  "Location Hours": "OfficeHours",
  "Call Center Hours": "CallCenterHours",
  "FMS Integration": "FMSIntegrationId",
  "FMS Integration ID": "FMSIntegrationId",
  "FMS ID": "FMSId",
  "Location Website URL": "WebsiteURL",
  "URL Lookup Pattern\n(Leave blank - swivl will fill this out)": "URLLookupPattern",
  "After Hours Phone #": "AfterHoursPhoneNumber",
  "DM Name": "DMName",
  "DM Email": "DMEmail",
  Brand: "BrandId",
  "DaVinci Code Integration": "HasDaVinciCodeIntegration",
  "About Us Page": "HasAboutURL",
  "About Us URL": "AboutStorageCompanyURL",
  "Account Login Page": "HasAccountLoginPage",
  "Account Login URL": "AccountLoginURL",
  "Auctions Page": "HasAuctions",
  "Auctions URL": "AuctionsURL",
  "Careers Page": "HasCareerURL",
  "Careers URL": "CareersURL",
  "Insurance Info Page": "HasInsuranceWebsiteURL",
  "Insurance Info URL": "InsuranceURL",
  "Moving Services": "HasMovingServices",
  "Moving Services URL": "MovingServicesURL",
  "Packing Supplies": "HasPackingSupplies",
  "Packing Supplies URL": "PackingSuppliesURL",
  "Pay By Phone": "HasPayByPhone",
  "Pay by Phone #": "PayByPhoneNumber",
  "Pay Online": "HasPayOnlineURL",
  "Pay Online Login URL\nIf it's the same across all locations": "PayOnlineURL",
  "Location Specific Payment Portal URL\nIf it's unique per location": "PaymentURL",
  "Recycle Program": "HasRecycleProgram",
  "Recycle Program URL": "RecycleURL",
  "Referral Program": "HasReferralProgram",
  "Referral Program URL": "ReferralProgramURL",
  "Shred Program": "HasShredProgram",
  "Shred Program URL": "ShredURL",
  "Size Guide": "HasSizeGuideURL",
  "Size Visuals URL": "SizeVisualsURL",
  "Privacy Policy": "HasPrivacyPolicyURL",
  "Privacy Policy URL": "PrivacyPolicyURL",
  "24 Hour Access": "Has24HourAccess",
  "After Hours Access": "HasAfterHoursAccess",
  Appointments: "HasAppointments",
  "Climate Controlled Units": "HasClimateControlledUnits",
  "Conference Room": "HasConferenceRoom",
  "Deliveries Accepted": "HasDeliveriesAccepted",
  "Drive-Up Units": "HasDriveUpUnits",
  Dollies: "HasDollies",
  Dumpster: "HasDumpsters",
  "Electricity In Unit": "HasElectricityInUnit",
  Elevators: "HasElevators",
  "Heated Units": "HasHeatedUnits",
  "Loading Area Type": "LoadingAreaType",
  "Lock Type": "LockTypes",
  "Moving Truck": "HasMovingTrucks",
  "Moving Truck Type": "TruckTypes",
  Notary: "HasNotary",
  "Office Space": "HasOfficeSpace",
  "On Location Parking Type": "ParkingType",
  "Padlock Codes": "HasPadlocks",
  "Payment Options": "PaymentOptions",
  "Security Amenities": "SecurityAmenities",
  "Video Surveillance": "HasVideoSurveillance",
  "Vehicle Storage": "HasVehicleStorage",
  "Small Vehicle": "HasSmallVehicleStorage",
  "Indoor Vehicle": "HasIndoorVehicleStorage",
  "Outdoor Vehicle": "HasOutdoorVehicleStorage",
  "Covered Vehicle": "HasCoveredVehicleStorage",
  RV: "HasRVParking",
  "Covered RV": "HasRVParkingCovered",
  "Enclosed RV": "HasRVParkingEnclosed",
  Trailer: "HasTrailerStorage",
  "Business Storage": "HasBusinessStorage",
  Lockers: "HasLockers",
  Wine: "HasWineStorage",
  Boat: "HasBoatStorage",
  "Photo URL": "AvatarURL",
  "QR Link Slug": "SpotLocationSlug",
  "QR Link Short Slug": "SpotLinkSlug",
  // org vars
  Tier: "whichTier",
  "Primary Brand Color": "BrandColor"
};

export function ValidationAndImportView() {
  const [sheetUrl, setSheetUrl] = useState("");
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [sheetData, setSheetData] = useState<GoogleSheetData | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState<TestModeResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isSingleLocation, setIsSingleLocation] = useState(false);

  useEffect(() => {
    if (sheetUrl.trim()) {
      try {
        const sheetId = extractSheetId(sheetUrl);
        loadGoogleSheetNames(sheetId, setIsLoadingSheets, setAvailableSheets, setSelectedSheet);
      } catch (error) {
        setAvailableSheets([]);
        setSelectedSheet("");
      }
    }
    // Clear results when URL changes
    setSheetData(null);
    setValidationErrors([]);
    setTestResults(null);
  }, [sheetUrl]);

  // Clear results when sheet selection changes
  useEffect(() => {
    setSheetData(null);
    setValidationErrors([]);
    setTestResults(null);
    setImportResult(null);
  }, [selectedSheet]);

  const handleValidate = async () => {
    setIsValidating(true);
    setValidationErrors([]);

    try {
      const sheetId = extractSheetId(sheetUrl);
      const range = selectedSheet ? `${selectedSheet}!A:ZZ` : "A:ZZ";
      const response = await API.Google.readGoogleSheet(sheetId, range);

      const data = {
        headers: response.headers,
        records: response.records
      };

      setSheetData(data);

      // Run validation
      const allErrors: ValidationError[] = [];
      data.records.forEach((record, index) => {
        const recordErrors = validateRecord(record, data.records, index, data.headers);
        allErrors.push(...recordErrors);
      });

      setValidationErrors(allErrors);

      // Clear all highlights first, then highlight error cells
      const dataRange = selectedSheet ? `${selectedSheet}!A2:ZZ${data.records.length + 1}` : `A2:ZZ${data.records.length + 1}`;

      // Get all cell values to preserve them
      const allCellValues = data.records.map((record) => data.headers.map((header) => record[header] || ""));

      // Clear all highlights
      await API.Google.updateGoogleSheet(sheetId, dataRange, allCellValues, {
        backgroundColor: "#FFFFFF",
        sheetName: selectedSheet
      });

      // Highlight error cells in light red
      if (allErrors.length > 0) {
        for (const error of allErrors) {
          const colLetter = getColumnLetter(error.colIndex);
          const cellRange = selectedSheet ? `${selectedSheet}!${colLetter}${error.rowIndex}` : `${colLetter}${error.rowIndex}`;

          // Get the actual cell value from the data
          const cellValue = data.records[error.rowIndex - 2][error.field] || "";

          await API.Google.updateGoogleSheet(sheetId, cellRange, [[cellValue]], {
            backgroundColor: "#ffcccc",
            sheetName: selectedSheet
          });
        }
      }
    } catch (error) {
      console.error("Error:", error);
      AlertsModel.errorToast(`Failed to load sheet: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsValidating(false);
    }
  };

  const handleTestMode = () => {
    if (!sheetData) return;

    setIsTesting(true);

    try {
      const orgLevelVars: Array<{ key: string; value: string; columnName: string }> = [];
      const locationLevelVars: Array<{
        locationId: string;
        locationName: string;
        variables: Array<{ key: string; value: string; columnName: string }>;
      }> = [];
      const skippedColumns: string[] = [];

      // Analyze each column
      sheetData.headers.forEach((header) => {
        const varKey = COLUMN_MAPPING[header];
        if (!varKey) return; // Skip unmapped columns

        // Skip Name (already created in Step 2)
        if (varKey === "Name") {
          skippedColumns.push(header);
          return;
        }

        // FMSIntegrationId is ALWAYS location-level
        if (varKey === "FMSIntegrationId") {
          sheetData.records.forEach((record) => {
            const value = normalizeValue(record[header]);
            if (value) {
              let locationEntry = locationLevelVars.find((l) => l.locationId === record["Location ID"]);
              if (!locationEntry) {
                locationEntry = {
                  locationId: record["Location ID"],
                  locationName: record["Location Name"],
                  variables: []
                };
                locationLevelVars.push(locationEntry);
              }
              locationEntry.variables.push({ key: varKey, value, columnName: header });
            }
          });
          return;
        }

        // Check if all locations have the same value
        const values = sheetData.records.map((r) => normalizeValue(r[header])).filter((v) => v !== ""); // Ignore empty values

        if (values.length === 0) return; // No data in this column

        const uniqueValues = [...new Set(values)];

        if (!isSingleLocation && uniqueValues.length === 1 && values.length === sheetData.records.length) {
          // All locations have the same value → org-level
          orgLevelVars.push({ key: varKey, value: uniqueValues[0], columnName: header });
        } else {
          // Different values → location-level
          sheetData.records.forEach((record) => {
            const value = normalizeValue(record[header]);
            if (value) {
              let locationEntry = locationLevelVars.find((l) => l.locationId === record["Location ID"]);
              if (!locationEntry) {
                locationEntry = {
                  locationId: record["Location ID"],
                  locationName: record["Location Name"],
                  variables: []
                };
                locationLevelVars.push(locationEntry);
              }
              locationEntry.variables.push({ key: varKey, value, columnName: header });
            }
          });
        }
      });

      const totalLocationVars = locationLevelVars.reduce((sum, loc) => sum + loc.variables.length, 0);

      setTestResults({
        orgLevelVars,
        locationLevelVars,
        summary: {
          totalOrgVars: orgLevelVars.length,
          totalLocationVars,
          totalLocations: locationLevelVars.length,
          skippedColumns
        }
      });
    } catch (error) {
      console.error("Error in test mode:", error);
      AlertsModel.errorToast("Test mode failed");
    } finally {
      setIsTesting(false);
    }
  };

  const handleImport = async () => {
    if (!testResults) return;
    if (!Member.orgId) {
      AlertsModel.errorToast("No org ID found");
      return;
    }

    setIsImporting(true);
    setImportProgress({ current: 0, total: 0 });
    setImportResult(null);

    const result: ImportResult = {
      created: 0,
      updated: 0,
      failed: 0,
      errors: []
    };

    try {
      const totalVars = testResults.summary.totalOrgVars + testResults.summary.totalLocationVars;
      setImportProgress({ current: 0, total: totalVars });

      let currentProgress = 0;

      // Import org-level variables
      for (const orgVar of testResults.orgLevelVars) {
        try {
          await API.LocationVars.createVariable(
            orgVar.key,
            orgVar.value,
            Member.orgId,
            undefined, // groupId
            undefined // locationId (undefined = org-level)
          );
          result.created++;
          currentProgress++;
          setImportProgress({ current: currentProgress, total: totalVars });
        } catch (error) {
          result.failed++;
          result.errors.push(`Org-level ${orgVar.key}: ${error instanceof Error ? error.message : "Unknown error"}`);
        }

        // Small delay to avoid overwhelming server
        if (currentProgress % 10 === 0) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Import location-level variables
      for (const location of testResults.locationLevelVars) {
        for (const locationVar of location.variables) {
          try {
            await API.LocationVars.createVariable(
              locationVar.key,
              locationVar.value,
              Member.orgId,
              undefined, // groupId
              parseInt(location.locationId)
            );
            result.created++;
            currentProgress++;
            setImportProgress({ current: currentProgress, total: totalVars });
          } catch (error) {
            result.failed++;
            result.errors.push(`${location.locationName} - ${locationVar.key}: ${error instanceof Error ? error.message : "Unknown error"}`);
          }

          // Small delay every 10 variables
          if (currentProgress % 10 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      }

      setImportResult(result);
    } catch (error) {
      console.error("Error during import:", error);
      AlertsModel.errorToast("Import failed");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>6️⃣ Validate & Import the Google Sheet</h2>
      <p style={styles.description}>
        Once the operator has confirmed their Google Sheet is complete, validate and import location-level data. This is what fully populates Property
        Manager with the info from the sheet.
      </p>

      <div style={styles.requirementsBox}>
        <h4 style={styles.heading}>Requirements:</h4>
        <ol style={styles.description}>
          <li>📝 swivl Location IDs exist in your Google Sheet</li>
          <li>🫡 Operator has confirmed that everything is ready to be imported</li>
        </ol>
      </div>

      <div style={styles.inputWrapper}>
        <input
          type="url"
          value={sheetUrl}
          onChange={(e) => setSheetUrl(e.target.value)}
          placeholder="Google Sheet URL: https://docs.google.com/spreadsheets/d/..."
          disabled={isValidating}
          style={styles.input}
        />
      </div>

      {availableSheets.length > 1 && (
        <div style={styles.inputWrapper}>
          <select
            value={selectedSheet}
            onChange={(e) => setSelectedSheet(e.target.value)}
            disabled={isValidating || isLoadingSheets}
            style={styles.input}
          >
            <option value="">Select a sheet...</option>
            {availableSheets.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoadingSheets && <div style={styles.loadingText}>⏳ Loading sheets...</div>}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          margin: "12px 0",
          padding: "10px 12px",
          backgroundColor: "#fff7ed",
          border: "1px solid #fed7aa",
          borderRadius: "6px"
        }}
      >
        <input
          type="checkbox"
          id="singleLocation"
          checked={isSingleLocation}
          onChange={(e) => {
            setIsSingleLocation(e.target.checked);
            setTestResults(null);
            setImportResult(null);
          }}
          style={{ width: "16px", height: "16px", cursor: "pointer" }}
        />
        <label htmlFor="singleLocation" style={{ fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
          ⚠️ IMPORTANT: Check if this is a single location operator
        </label>
      </div>

      <div style={styles.buttonGroup}>
        <Button
          onClick={handleValidate}
          disabled={isValidating || !sheetUrl.trim() || (availableSheets.length > 1 && !selectedSheet) || isLoadingSheets}
        >
          {isValidating ? (
            <>
              <Spinner /> Validating...
            </>
          ) : (
            "Validate Data"
          )}
        </Button>

        <Button onClick={handleTestMode} disabled={!sheetData || isTesting}>
          {isTesting ? (
            <>
              <Spinner /> Testing...
            </>
          ) : (
            "Test Import"
          )}
        </Button>

        {testResults && (
          <Button onClick={handleImport} disabled={isImporting}>
            {isImporting ? (
              <>
                <Spinner /> Importing...
              </>
            ) : (
              "Import"
            )}
          </Button>
        )}
      </div>

      {isImporting && (
        <div style={{ ...styles.successBox, marginBottom: "20px" }}>
          <h3 style={styles.successHeading}>🔄 Importing Variables...</h3>
          <div style={{ fontSize: "14px", color: "#374151" }}>
            <p>
              Progress: {importProgress.current} / {importProgress.total} variables
            </p>
            <div style={{ width: "100%", height: "8px", backgroundColor: "#e5e7eb", borderRadius: "4px", marginTop: "8px", overflow: "hidden" }}>
              <div
                style={{
                  width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%`,
                  height: "100%",
                  backgroundColor: "#10b981",
                  transition: "width 0.3s ease"
                }}
              />
            </div>
          </div>
        </div>
      )}

      {sheetData && validationErrors.length === 0 && (
        <div style={styles.successBox}>
          <h3 style={styles.successHeading}>✅ Validation Passed</h3>
          <div style={{ fontSize: "14px", color: "#374151" }}>
            <p>{sheetData.records.length} locations validated, 0 errors</p>
          </div>
        </div>
      )}

      {validationErrors.length > 0 &&
        (() => {
          // Group errors by field only (combine all errors for same field)
          const groupedErrors: Record<string, { field: string; errors: string[]; rows: Set<number> }> = {};
          validationErrors.forEach((err) => {
            if (!groupedErrors[err.field]) {
              groupedErrors[err.field] = { field: err.field, errors: [], rows: new Set() };
            }
            if (!groupedErrors[err.field].errors.includes(err.error)) {
              groupedErrors[err.field].errors.push(err.error);
            }
            groupedErrors[err.field].rows.add(err.rowIndex);
          });

          const uniqueColumns = Object.keys(groupedErrors).length;

          return (
            <div style={styles.errorBox}>
              <h3 style={styles.errorHeading}>❌ Validation Errors</h3>
              <div style={{ fontSize: "14px", color: "#374151" }}>
                <p>
                  <strong>
                    Found errors in {uniqueColumns} column{uniqueColumns !== 1 ? "s" : ""}:
                  </strong>
                </p>
                <ul style={{ margin: "8px 0", paddingLeft: "20px", fontSize: "13px", listStyle: "none" }}>
                  {Object.values(groupedErrors).map((group, index) => (
                    <li key={index} style={{ margin: "8px 0" }}>
                      <strong>{group.field}</strong> ({group.errors.join(", ")})<br />
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        Rows:{" "}
                        {Array.from(group.rows)
                          .sort((a, b) => a - b)
                          .join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
                <p style={{ marginTop: "12px", fontSize: "13px", color: "#666" }}>⚠️ You can still proceed to import, but errors may cause issues.</p>
              </div>
            </div>
          );
        })()}

      {testResults && (
        <div style={{ ...styles.successBox, marginTop: "20px" }}>
          <h3 style={styles.successHeading}>🧪 Test Mode Results</h3>
          <p style={{ fontSize: "14px", marginBottom: "10px" }}>
            <strong>{testResults.summary.totalOrgVars}</strong> org-level, <strong>{testResults.summary.totalLocationVars}</strong> location-level
            variables across <strong>{testResults.summary.totalLocations}</strong> locations
          </p>
          {testResults.orgLevelVars.length > 0 && (
            <details style={{ fontSize: "13px", marginTop: "10px" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>View org-level ({testResults.orgLevelVars.length})</summary>
              <div style={{ maxHeight: "150px", overflowY: "auto", marginTop: "5px", fontSize: "12px" }}>
                {testResults.orgLevelVars.map((v, i) => (
                  <div key={i}>
                    {v.key} = {v.value}
                  </div>
                ))}
              </div>
            </details>
          )}
          {testResults.locationLevelVars.length > 0 && (
            <details style={{ fontSize: "13px", marginTop: "10px" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                View location-level (showing {Math.min(3, testResults.locationLevelVars.length)} of {testResults.locationLevelVars.length})
              </summary>
              <div style={{ maxHeight: "200px", overflowY: "auto", marginTop: "5px", fontSize: "12px" }}>
                {testResults.locationLevelVars.slice(0, 3).map((loc, i) => (
                  <div key={i} style={{ marginBottom: "8px" }}>
                    <strong>{loc.locationName}:</strong> {loc.variables.map((v) => `${v.key}=${v.value}`).join(", ")}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {importResult && (
        <div style={{ ...styles.successBox, marginTop: "20px" }}>
          <h3 style={styles.successHeading}>✅ Import Complete</h3>
          <div style={{ fontSize: "14px", color: "#374151" }}>
            <p style={{ marginBottom: "10px" }}>
              <strong>Created/Updated:</strong> {importResult.created} variables
            </p>
            {importResult.failed > 0 && (
              <>
                <p style={{ marginBottom: "8px", color: "#dc2626" }}>
                  <strong>Failed:</strong> {importResult.failed} variables
                </p>
                <details style={{ fontSize: "13px", marginTop: "8px" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>View errors ({importResult.errors.length})</summary>
                  <ul style={{ fontSize: "12px", margin: "8px 0", paddingLeft: "20px", maxHeight: "200px", overflowY: "auto" }}>
                    {importResult.errors.map((error, i) => (
                      <li key={i} style={{ margin: "4px 0" }}>
                        {error}
                      </li>
                    ))}
                  </ul>
                </details>
                <p style={{ marginTop: "12px", fontSize: "13px", color: "#666" }}>
                  💡 You can fix errors in the sheet and re-run import. Upsert will update existing variables.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
